import { Layer } from '../../context/context';
import { Fleet } from '../../components/map/components/FleetMenu';
import { v4 as uuid4 } from 'uuid';
import { deserializeFleetFromKml } from './fleetKmlGenerator';
import { deserializeFixedRoutesFromKml } from './fixedRoutesKmlGenerator';

// Legacy data structure types (matching old system)
interface LegacyPlaceMark {
  id?: string;  // Shape ID - must persist for originPointId/destPointId references
  label: string;
  type: "point" | "polygon" | "line";
  latitude?: number;
  longitude?: number;
  geometry?: [number, number][];
  layerId: number;
  // Color and style properties
  colour?: string;
  borderColour?: string;
  opacity?: string;
  borderWidth?: number;
  icon?: string;
  size?: number;
  // Ride/trip-specific fields (for line type)
  time?: string | number | null;
  request_time?: string | null;
  numPassengers?: number | string | null;
  wheelchair?: number | string | null;
  ride_id?: string;
  user_id?: string;
  standard_seat?: number;
  request_type?: string;
  originPointId?: string;
  destPointId?: string;
  _timeType?: 'string' | 'number';
}

interface LegacyLayer {
  layerName: string;
  placeMarks: LegacyPlaceMark[];
}

interface LegacyKMLResult {
  mapName: string;
  layers: LegacyLayer[];
  fleets: Fleet[];
  fixedRoutes?: { routes: any[]; stops: any[] };
}

/**
 * Extract ExtendedData from a Placemark element
 */
function extractExtendedData(placemark: Element): Partial<LegacyPlaceMark> {
  const extendedData: Partial<LegacyPlaceMark> = {};
  const extendedDataElement = placemark.querySelector('ExtendedData');
  
  if (!extendedDataElement) {
    return extendedData;
  }
  
  const dataElements = extendedDataElement.querySelectorAll('Data');
  dataElements.forEach(dataElement => {
    const name = dataElement.getAttribute('name');
    const valueElement = dataElement.querySelector('value');
    const value = valueElement?.textContent || '';
    
    if (name === 'id') {
      extendedData.id = value;
    } else if (name === 'colour') {
      extendedData.colour = value;
    } else if (name === 'borderColour') {
      extendedData.borderColour = value;
    } else if (name === 'opacity') {
      extendedData.opacity = value;
    } else if (name === 'borderWidth') {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        extendedData.borderWidth = numValue;
      }
    } else if (name === 'icon') {
      extendedData.icon = value;
    } else if (name === 'size') {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        extendedData.size = numValue;
      }
    } else if (name === 'time') {
      extendedData.time = value;
    } else if (name === 'request_time') {
      extendedData.request_time = value;
    } else if (name === 'numPassengers') {
      const numValue = parseInt(value, 10);
      extendedData.numPassengers = !isNaN(numValue) ? numValue : value;
    } else if (name === 'wheelchair') {
      const numValue = parseInt(value, 10);
      extendedData.wheelchair = !isNaN(numValue) ? numValue : value;
    } else if (name === 'ride_id') {
      extendedData.ride_id = value;
    } else if (name === 'user_id') {
      extendedData.user_id = value;
    } else if (name === 'standard_seat') {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        extendedData.standard_seat = numValue;
      }
    } else if (name === 'request_type') {
      extendedData.request_type = value;
    } else if (name === 'originPointId') {
      extendedData.originPointId = value;
    } else if (name === 'destPointId') {
      extendedData.destPointId = value;
    } else if (name === '_timeType') {
      extendedData._timeType = value === 'number' ? 'number' : value === 'string' ? 'string' : undefined;
    }
  });
  
  return extendedData;
}

/**
 * Legacy load_kml function that matches the old system exactly
 * This returns the old placeMarks structure that the system expects
 * Now includes fleet data extraction
 */
export default function load_kml(kmlString: string): LegacyKMLResult {
  const parser = new DOMParser();
  const kmlDoc = parser.parseFromString(kmlString, "application/xml");

  const layers: LegacyLayer[] = [];
  const documentElement = kmlDoc.querySelector("Document");
  
  if (!documentElement) {
    throw new Error("No Document element found in KML");
  }
  
  const mapName = documentElement.querySelector("name")?.textContent || "Untitled Map";

  const folders = documentElement.querySelectorAll("Folder");
  console.log('🔍 Found', folders.length, 'Folder elements in KML');
  
  // Log all folder names for debugging
  folders.forEach((folder, idx) => {
    const folderName = folder.querySelector("name")?.textContent || `Unnamed Folder ${idx}`;
    const placemarkCount = folder.querySelectorAll("Placemark").length;
    console.log(`  Folder ${idx}: "${folderName}" with ${placemarkCount} Placemarks`);
  });
  folders.forEach((folder, layerId) => {
    const layerName = folder.querySelector("name")?.textContent || `Layer ${layerId + 1}`;
    
    // Skip metadata folders (Fixed Routes Configuration, Fleet Configuration)
    // Also skip "Fixed Routes" layer - fixed routes are loaded from ExtendedData, not Placemarks
    // BUT: We still need to create an empty Fixed Routes layer if it exists in the KML
    // so that the layer structure is preserved
    if (layerName === "Fixed Routes Configuration" || 
        layerName === "Fleet Configuration") {
      console.log(`  Skipping folder "${layerName}" (loaded from ExtendedData)`);
      return;
    }
    
    // For "Fixed Routes" layer, create an empty layer (routes will be added by useFixedRoutesSync)
    if (layerName === "Fixed Routes") {
      console.log(`  Creating empty "${layerName}" layer (routes loaded from ExtendedData)`);
      layers.push({
        layerName: "Fixed Routes",
        placeMarks: [] // Empty - routes come from ExtendedData
      });
      return;
    }
    
    const placeMarks: LegacyPlaceMark[] = [];

    const placemarkElements = folder.querySelectorAll("Placemark");
    console.log(`  Processing ${placemarkElements.length} Placemarks in folder "${layerName}"`);
    
    placemarkElements.forEach((placemark, pmIdx) => {
      const label = placemark.querySelector("name")?.textContent || "Unnamed Placemark";
      console.log(`    Placemark ${pmIdx}: "${label}"`);

      // Handle points
      const pointElement = placemark.querySelector("Point");
      if (pointElement) {
        console.log(`      Found Point element`);
        const coordinates = pointElement.querySelector("coordinates")?.textContent?.trim();
        if (coordinates) {
          const [longitude, latitude] = coordinates.split(",").map(Number);
          if (!isNaN(longitude) && !isNaN(latitude)) {
            console.log(`      ✓ Parsed point: lat=${latitude}, lng=${longitude}`);
            const extendedData = extractExtendedData(placemark);
            placeMarks.push({
              label,
              type: "point",
              latitude,
              longitude,
              layerId,
              ...extendedData
            });
          } else {
            console.warn(`      ✗ Invalid point coordinates: "${coordinates}"`);
          }
        } else {
          console.warn(`      ✗ Point element has no coordinates`);
        }
      }

      // Handle polygons
      const polygonElement = placemark.querySelector("Polygon");
      if (polygonElement) {
        console.log(`      Found Polygon element`);
        const coordinates = polygonElement.querySelector("coordinates")?.textContent?.trim();
        if (coordinates) {
          const geometry = coordinates.split(" ").map(pair => {
            const [lon, lat] = pair.split(",").map(Number);
            if (!isNaN(lon) && !isNaN(lat)) {
              return [lat, lon] as [number, number];
            }
            return null;
          }).filter(pair => pair !== null) as [number, number][];
          
          if (geometry && geometry.length > 0) {
            console.log(`      ✓ Parsed polygon with ${geometry.length} points`);
            const extendedData = extractExtendedData(placemark);
            placeMarks.push({
              label,
              type: "polygon",
              geometry,
              layerId,
              ...extendedData
            });
          } else {
            console.warn(`      ✗ Polygon has no valid geometry`);
          }
        } else {
          console.warn(`      ✗ Polygon element has no coordinates`);
        }
      }
      
      // Handle lines (LineString) - parse as line type
      const lineStringElement = placemark.querySelector("LineString");
      if (lineStringElement) {
        console.log(`      Found LineString element`);
        const coordinates = lineStringElement.querySelector("coordinates")?.textContent?.trim();
        if (coordinates) {
          const geometry = coordinates.split(" ").map(pair => {
            const [lon, lat] = pair.split(",").map(Number);
            if (!isNaN(lon) && !isNaN(lat)) {
              return [lat, lon] as [number, number];
            }
            return null;
          }).filter(pair => pair !== null) as [number, number][];
          
          if (geometry && geometry.length > 0) {
            console.log(`      ✓ Parsed LineString with ${geometry.length} points as line`);
            const extendedData = extractExtendedData(placemark);
            placeMarks.push({
              label,
              type: "line",
              geometry,
              layerId,
              ...extendedData
            });
          } else {
            console.warn(`      ✗ LineString has no valid geometry`);
          }
        } else {
          console.warn(`      ✗ LineString element has no coordinates`);
        }
      }
      
      // If no geometry type was found, log it
      if (!pointElement && !polygonElement && !lineStringElement) {
        console.warn(`      ✗ Placemark has no Point, Polygon, or LineString element`);
        console.warn(`      Placemark HTML:`, placemark.outerHTML.substring(0, 200));
      }
    });
    
    console.log(`  Folder "${layerName}" resulted in ${placeMarks.length} placeMarks`);

    if (placeMarks.length > 0) {
      layers.push({
        layerName,
        placeMarks,
      });
    }
  });

  // If no folders found, process placemarks directly
  if (layers.length === 0) {
    const placeMarks: LegacyPlaceMark[] = [];
    const placemarkElements = documentElement.querySelectorAll("Placemark");
    
    placemarkElements.forEach(placemark => {
      const label = placemark.querySelector("name")?.textContent || "Unnamed Placemark";

      // Handle points
      const pointElement = placemark.querySelector("Point");
      if (pointElement) {
        const coordinates = pointElement.querySelector("coordinates")?.textContent?.trim();
        if (coordinates) {
          const [longitude, latitude] = coordinates.split(",").map(Number);
          if (!isNaN(longitude) && !isNaN(latitude)) {
            const extendedData = extractExtendedData(placemark);
            placeMarks.push({
              label,
              type: "point",
              latitude,
              longitude,
              layerId: 0,
              ...extendedData
            });
          }
        }
      }

      // Handle polygons
      const polygonElement = placemark.querySelector("Polygon");
      if (polygonElement) {
        const coordinates = polygonElement.querySelector("coordinates")?.textContent?.trim();
        if (coordinates) {
          const geometry = coordinates.split(" ").map(pair => {
            const [lon, lat] = pair.split(",").map(Number);
            if (!isNaN(lon) && !isNaN(lat)) {
              return [lat, lon] as [number, number];
            }
            return null;
          }).filter(pair => pair !== null) as [number, number][];
          
          if (geometry && geometry.length > 0) {
            const extendedData = extractExtendedData(placemark);
            placeMarks.push({
              label,
              type: "polygon",
              geometry,
              layerId: 0,
              ...extendedData
            });
          }
        }
      }

      // Handle lines (LineString) - parse as line type
      const lineStringElement = placemark.querySelector("LineString");
      if (lineStringElement) {
        const coordinates = lineStringElement.querySelector("coordinates")?.textContent?.trim();
        if (coordinates) {
          const geometry = coordinates.split(" ").map(pair => {
            const [lon, lat] = pair.split(",").map(Number);
            if (!isNaN(lon) && !isNaN(lat)) {
              return [lat, lon] as [number, number];
            }
            return null;
          }).filter(pair => pair !== null) as [number, number][];
          
          if (geometry && geometry.length > 0) {
            const extendedData = extractExtendedData(placemark);
            placeMarks.push({
              label,
              type: "line",
              geometry,
              layerId: 0,
              ...extendedData
            });
          }
        }
      }
    });

    if (placeMarks.length > 0) {
      layers.push({
        layerName: "Default Layer",
        placeMarks
      });
    }
  }

  // Extract fleet data from KML
  const fleets = deserializeFleetFromKml(kmlString);
  
  // Extract fixed routes data from KML
  let fixedRoutes: { routes: any[]; stops: any[] } | undefined;
  try {
    fixedRoutes = deserializeFixedRoutesFromKml(kmlString);
  } catch (error) {
    console.warn('Error extracting fixed routes from KML:', error);
    fixedRoutes = { routes: [], stops: [] };
  }
  
  return { mapName, layers, fleets, fixedRoutes };
}

/**
 * Convert legacy placeMarks structure to current Layer structure
 * Use this to bridge between old and new systems
 */
export function convertLegacyToCurrentLayers(legacyLayers: LegacyLayer[]): Layer[] {
  return legacyLayers.map((legacyLayer, index) => ({
    layerId: index,
    layerName: legacyLayer.layerName,
    hidden: false,
    expanded: true,
    shapes: legacyLayer.placeMarks.map(mark => {
      // Preserve shape ID when present - critical for originPointId/destPointId references
      const baseShape = {
        id: mark.id || uuid4(),
        layerId: index,
        name: mark.label,
        description: '',
        isLocked: false,
        hidden: false
      };

      if (mark.type === 'point') {
        return {
          ...baseShape,
          type: 'point' as const,
          coordinates: [mark.latitude!, mark.longitude!] as [number, number],
          colour: mark.colour || '#ff0000',
          icon: (mark.icon as any) || 'default',
          size: mark.size || 24
        };
      } else if (mark.type === 'polygon') {
        return {
          ...baseShape,
          type: 'polygon' as const,
          coordinates: [mark.geometry!] as [number, number][][],
          colour: mark.colour || '#ff0000',
          opacity: mark.opacity || '0.5',
          borderColour: mark.borderColour || '#000000',
          borderWidth: mark.borderWidth || 2
        };
      } else if (mark.type === 'line') {
        // Restore time with correct type using _timeType hint
        let timeValue: string | number | null | undefined = mark.time ?? mark.request_time;
        if (timeValue !== undefined && timeValue !== null && mark._timeType === 'number') {
          const numVal = Number(timeValue);
          timeValue = !isNaN(numVal) ? numVal : timeValue;
        } else if (timeValue !== undefined && timeValue !== null && typeof timeValue !== 'string') {
          timeValue = String(timeValue);
        }
        // When both time and request_time exist, use both; when only one exists, set both for compatibility
        const requestTimeStr = typeof timeValue === 'string' ? timeValue : (mark.request_time ?? (timeValue != null ? String(timeValue) : undefined));
        return {
          ...baseShape,
          type: 'line' as const,
          coordinates: mark.geometry! as [number, number][],
          colour: mark.colour || '#ff0000',
          opacity: mark.opacity || '1.0',
          borderColour: mark.borderColour || mark.colour || '#ff0000',
          borderWidth: mark.borderWidth || 6,
          // Restore ride/trip fields from ExtendedData
          time: timeValue ?? undefined,
          ...(requestTimeStr && { request_time: requestTimeStr }),
          ...(mark.numPassengers !== undefined && mark.numPassengers !== null && { numPassengers: mark.numPassengers }),
          ...(mark.wheelchair !== undefined && mark.wheelchair !== null && { wheelchair: mark.wheelchair }),
          ...(mark.ride_id && { ride_id: mark.ride_id }),
          ...(mark.user_id && { user_id: mark.user_id }),
          ...(mark.standard_seat !== undefined && { standard_seat: mark.standard_seat }),
          ...(mark.request_type && { request_type: mark.request_type }),
          ...(mark.originPointId && { originPointId: mark.originPointId }),
          ...(mark.destPointId && { destPointId: mark.destPointId })
        };
      }
      
      return null;
    }).filter(shape => shape !== null)
  })) as Layer[];
}

// Export the legacy function name for backward compatibility
export { load_kml }; 